import { CatalogFiltersData } from '../../../types'
import { FiltersState } from './_state'

/**
 * Класс для управления URL параметрами фильтров
 */
export class FiltersURL {
  /**
   * Параметры, которыми управляет каталог. Только они удаляются при обновлении
   * URL — всё остальное (метки трафика, `date`/`start_date` для расписания и
   * прочие чужие параметры) должно пережить перерисовку фильтров.
   *
   * `start_date`/`end_date` сюда не входят: ими уже владеет расписание
   * квеста/города (Timetable/Datepicker, ISO-строка вида «2026-09-19»).
   * До появления реального date-range фильтра в каталоге эти два параметра
   * читались и перезаписывались как unix-таймстемп (`Date#getTime()`), и на
   * странице расписания моментально портили дату друг друга: и `filters.dates`
   * нигде дальше не используется (ни в filterCatalogQuests, ни в списке) —
   * убираем, а не чиним, раз фильтр по датам ещё не реализован.
   */
  private static readonly OWN_PARAMS: string[] = [
    'type',
    'category',
    'complexity',
    'horror',
    'players',
    'players_count',
    'lang',
    'time',
    'metro',
    'age',
    'original'
  ]

  private state: FiltersState

  /**
   * Создаёт экземпляр FiltersURL
   *
   * @param {FiltersState} state - Состояние фильтров
   */
  constructor(state: FiltersState) {
    this.state = state
  }

  /**
   * Обновляет URL на основе текущих фильтров
   *
   * @param {CatalogFiltersData} filters - Данные фильтров
   */
  public updateURL(filters: CatalogFiltersData): void {
    const url = new URL(window.location.href)

    // Очищаем только свои параметры, чужие оставляем нетронутыми
    FiltersURL.OWN_PARAMS.forEach((param) => url.searchParams.delete(param))

    if (filters.type) {
      url.searchParams.set('type', filters.type)
    }

    if (filters.categories && filters.categories.length > 0) {
      url.searchParams.set('category', this.state.getCategories().join(','))
    }

    if (filters.complexity && filters.complexity.length > 0) {
      url.searchParams.set('complexity', filters.complexity.join(','))
    }

    if (filters.horror && filters.horror.length > 0) {
      url.searchParams.set('horror', filters.horror.join(','))
    }

    if (filters.haveEnglishVersion) {
      url.searchParams.set('lang', 'en')
    }

    if (filters.original) {
      url.searchParams.set('original', '1')
    }

    if (filters.time) {
      url.searchParams.set('time', filters.time)
    }

    if (filters.metro && filters.metro.length > 0) {
      url.searchParams.set('metro', filters.metro.join(','))
    }

    if (filters.age) {
      url.searchParams.set('age', filters.age)
    }

    if (filters.playersCount) {
      url.searchParams.set('players_count', String(filters.playersCount))
    }

    window.history.replaceState({}, '', url)
  }

  /**
   * Загружает фильтры из URL параметров
   */
  public loadFromURL(): void {
    const searchParams = new URLSearchParams(window.location.search)

    const type = searchParams.get('type')
    if (type) {
      this.state.setType(type)
    }

    const category = searchParams.get('category')
    if (category) {
      this.state.setCategories(category.split(','))
    }

    const complexity = searchParams.get('complexity')
    if (complexity) {
      this.state.setComplexity(complexity.split(','))
    }

    const horror = searchParams.get('horror')
    if (horror) {
      this.state.setHorror(horror.split(','))
    }

    const metro = searchParams.get('metro')
    if (metro) {
      this.state.setMetro(metro.split(','))
    }

    const age = searchParams.get('age')
    if (age && /^\d+$/.test(age) && Number(age) > 0) {
      this.state.setAge(age)
    }

    // «Только Клаустрофобия»: ссылка с original=1 открывает каталог уже отфильтрованным.
    if (searchParams.get('original') === '1') {
      this.state.setOriginal(true)
    }

    const additionalFilters = this.state.getAdditionalFilters()

    const lang = searchParams.get('lang')
    if (lang) {
      additionalFilters.haveEnglishVersion = lang === 'en'
    }
    // Иначе по ссылке с lang=en список отфильтрован, а кнопка сброса неактивна.
    if (additionalFilters.haveEnglishVersion) {
      this.state.setAdditionalFilters(additionalFilters, true)
    }

    const time = searchParams.get('time')
    if (time) {
      additionalFilters.time = time
    }

    const playersCount = searchParams.get('players_count')
    if (playersCount) {
      additionalFilters.playersCount = Number(playersCount)
    }
  }
}
